import React, { useState, useRef } from 'react';
import { accountsAPI } from '../services/api';
import { ImportData } from '../types';

const DataImport: React.FC = () => {
  const [importType, setImportType] = useState<'csv' | 'manual'>('csv');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess('');

    // Preview the file
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0]?.split(',').map(h => h.trim().replace(/"/g, ''));
      const previewData = lines.slice(1, 6).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row: any = {};
        headers?.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      }).filter(row => Object.values(row).some(v => v !== ''));
      
      setPreview(previewData);
    };
    reader.readAsText(selectedFile);
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', importType);

      const result = await accountsAPI.importData(formData);
      
      setSuccess(result.message || 'Data imported successfully! Your net worth history has been updated.');
      setFile(null);
      setPreview([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type: 'assets' | 'liabilities') => {
    const template = type === 'assets' 
      ? `type,account_type,institution,account,category,currency,1/1/2024,2/1/2024,3/1/2024,4/1/2024,5/1/2024
asset,personal,Chase Bank,Checking Account,Cash,CAD,5000.00,5200.00,4800.00,5500.00,6000.00
asset,personal,Vanguard,401k,Retirement,USD,25000.00,25500.00,26000.00,25800.00,26200.00
asset,personal,Robinhood,Investment Portfolio,Investments,USD,15000.00,15200.00,14800.00,15500.00,15800.00
asset,personal,Deutsche Bank,European Savings,Savings,EUR,8000.00,8200.00,8100.00,8300.00,8400.00
asset,business,My Company,Business Account,Business Cash,CAD,10000.00,12000.00,11000.00,13000.00,14000.00`
      : `type,account_type,institution,account,category,currency,1/1/2024,2/1/2024,3/1/2024,4/1/2024,5/1/2024
liability,personal,Chase Bank,Credit Card,Credit Cards,CAD,2500.00,2600.00,2400.00,2700.00,2800.00
liability,personal,Wells Fargo,Mortgage,Mortgage,CAD,300000.00,299500.00,299000.00,298500.00,298000.00
liability,personal,Barclays,UK Mortgage,Mortgage,GBP,200000.00,199500.00,199000.00,198500.00,198000.00
liability,business,Small Business Bank,Business Loan,Business Loans,CAD,50000.00,49500.00,49000.00,48500.00,48000.00`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_import_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="import-container">
      <div className="import-header">
        <h2>Import Historical Data</h2>
        <p className="import-subtitle">
          Easily import your financial history to see your net worth journey over time
        </p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      <div className="import-tabs">
        <button
          className={`import-tab ${importType === 'csv' ? 'active' : ''}`}
          onClick={() => setImportType('csv')}
        >
          📁 CSV Import
        </button>
        <button
          className={`import-tab ${importType === 'manual' ? 'active' : ''}`}
          onClick={() => setImportType('manual')}
        >
          ✏️ Manual Entry
        </button>
      </div>

      {importType === 'csv' && (
        <div className="import-section">
          <div className="template-section">
            <h3>📋 Download Templates</h3>
            <p>Use these templates to format your data correctly:</p>
            <div className="template-buttons">
              <button
                onClick={() => downloadTemplate('assets')}
                className="btn-secondary"
              >
                📈 Assets Template
              </button>
              <button
                onClick={() => downloadTemplate('liabilities')}
                className="btn-secondary"
              >
                📉 Liabilities Template
              </button>
            </div>
          </div>

          <div className="upload-section">
            <h3>📤 Upload Your Data</h3>
            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="file-input"
              />
              <div className="upload-area">
                <div className="upload-icon">📁</div>
                <p>Click to select CSV file or drag and drop</p>
                <p className="upload-hint">Required columns: type, account_type, institution, account, category, currency, then date columns (e.g., 5/14/2024)</p>
              </div>
            </div>

            {preview.length > 0 && (
              <div className="preview-section">
                <h4>📊 Data Preview</h4>
                <div className="preview-table">
                  <table>
                    <thead>
                      <tr>
                        {Object.keys(preview[0] || {}).map(header => (
                          <th key={header}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value, i) => (
                            <td key={i}>{String(value)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="preview-note">
                  Showing first {preview.length} rows. Total rows will be imported.
                </p>
              </div>
            )}

            {file && (
              <button
                onClick={handleImport}
                disabled={loading}
                className="btn-primary import-button"
              >
                {loading ? (
                  <>
                    <div className="loading-spinner-small"></div>
                    Importing...
                  </>
                ) : (
                  '🚀 Import Data'
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {importType === 'manual' && (
        <div className="import-section">
          <div className="manual-entry-info">
            <h3>✏️ Manual Entry</h3>
            <p>
              Prefer to enter data manually? You can add historical snapshots 
              for each account in the Assets and Liabilities pages. Each account 
              can have multiple snapshots with different dates.
            </p>
            <div className="manual-steps">
              <div className="step">
                <span className="step-number">1</span>
                <div>
                  <h4>Add Your Accounts</h4>
                  <p>Go to Assets or Liabilities and add your accounts</p>
                </div>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <div>
                  <h4>Add Historical Snapshots</h4>
                  <p>For each account, add historical values with dates</p>
                </div>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <div>
                  <h4>View Your Progress</h4>
                  <p>See your net worth trends on the Dashboard</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="import-tips">
        <h3>💡 Tips for Best Results</h3>
        <ul>
          <li><strong>Use consistent dates:</strong> M/D/YYYY format (e.g., 5/14/2024) for date columns</li>
          <li><strong>Include all accounts:</strong> Even small accounts add up over time</li>
                  <li><strong>Regular snapshots:</strong> Monthly or quarterly data shows trends best</li>
        <li><strong>Be consistent:</strong> Use the same categories and naming</li>
        <li><strong>Start simple:</strong> Import basic data first, then add details later</li>
        <li><strong>Date format:</strong> Use M/D/YYYY format for date column headers (e.g., 5/14/2024)</li>
        <li><strong>Account names:</strong> Use the 'account' column for account names (not 'name')</li>
        <li><strong>Currency codes:</strong> Use 3-letter currency codes (CAD, USD, EUR, GBP, JPY, AUD, CHF, CNY). Each account can have its own currency.</li>
        <li><strong>Header flexibility:</strong> Column headers are case-insensitive (e.g., "Type", "TYPE", or "type" all work).</li>
        <li><strong>Currency conversion:</strong> Values will be automatically converted to your display currency on the dashboard.</li>
        <li><strong>Flexible number formats:</strong> Supports $62,816.03, 62816.03, 62,816.03, (1,234.56), €1.234,56, etc.</li>
        </ul>
      </div>
    </div>
  );
};

export default DataImport; 